import { useState, useEffect } from 'react';
import { useAuth } from '@/supabase-auth';

export interface AuthReadyState {
  isReady: boolean;
  hasSession: boolean;
  hasAccessToken: boolean;
  reason: 'hydrating' | 'logged_out' | 'ready' | 'no_token_yet';
}

/**
 * Hook to gate data queries until auth session is fully hydrated with access_token.
 * Prevents premature queries that fail with 401/403 errors.
 * 
 * @returns {AuthReadyState} - Auth readiness state
 * - isReady: true when auth provider finished loading AND (user logged out OR has valid token)
 * - hasSession: true if session exists
 * - hasAccessToken: true if session has access_token
 * 
 * @example
 * const { isReady, hasAccessToken } = useAuthReady();
 * 
 * const { data } = useQuery({
 *   queryKey: ['family-members'],
 *   queryFn: fetchFamilyMembers,
 *   enabled: isReady && hasAccessToken  // Wait for token before querying
 * });
 * 
 * if (!isReady) return <PageLoader />;
 * if (isReady && !hasSession) return <Navigate to="/login" />;
 */
export function useAuthReady(): AuthReadyState {
  const { session, loading } = useAuth();
  const [isReady, setIsReady] = useState(false);
  const [reason, setReason] = useState<AuthReadyState['reason']>('hydrating');

  useEffect(() => {
    // Only mark ready if auth provider finished loading
    if (!loading) {
      if (!session) {
        // User is logged out - ready to show login
        setIsReady(true);
        setReason('logged_out');
      } else if (session.access_token) {
        // User has valid token - ready for authenticated queries
        // Note: Don't wait for profile as it may load async
        setIsReady(true);
        setReason('ready');
      } else {
        // Session exists but no token yet (rare edge case)
        setIsReady(false);
        setReason('no_token_yet');
      }
    } else {
      setIsReady(false);
      setReason('hydrating');
    }
  }, [loading, session, session?.access_token]);

  return {
    isReady,
    hasSession: !!session,
    hasAccessToken: !!session?.access_token,
    reason
  };
}
